import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, DataTable, inputCls, statusTone } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, FileText, Printer, Upload, Download, FileSignature, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Dokumen() {
  const { hasPerm, user } = useAuth();
  const isAdmin = user?.role === "Super Admin";
  const [templates, setTemplates] = useState([]);
  const [generated, setGenerated] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [upForm, setUpForm] = useState({ name: "", doc_type: "spk", auto_on_booking: false });
  const [genForm, setGenForm] = useState({ template_id: "", booking_id: "", project_id: "" });
  const [editTpl, setEditTpl] = useState(null);
  const [pv, setPv] = useState(null);
  const [pvUrl, setPvUrl] = useState(null);

  const openPreview = async () => {
    try {
      const r = await api.get(`/documents/preview-context?template_id=${genForm.template_id}${genForm.booking_id ? `&booking_id=${genForm.booking_id}` : ""}${genForm.project_id ? `&project_id=${genForm.project_id}` : ""}`);
      setPv(r.data);
      setPvUrl(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const renderPreview = async () => {
    try {
      const r = await api.post("/documents/preview-docx", { template_id: genForm.template_id, booking_id: genForm.booking_id || null, project_id: genForm.project_id || null, overrides: pv.fields }, { responseType: "blob" });
      setPvUrl(URL.createObjectURL(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [editForm, setEditForm] = useState({ name: "", doc_type: "spk", auto_on_booking: false, content: "" });
  const replaceFileRef = useRef(null);
  const fileRef = useRef(null);

  const openEditTpl = (t) => {
    setEditTpl(t);
    setEditForm({ name: t.name, doc_type: t.doc_type || "lainnya", auto_on_booking: !!t.auto_on_booking, content: t.content || "" });
  };

  const saveEditTpl = async () => {
    try {
      await api.put(`/documents/templates/${editTpl.id}`, editForm);
      toast.success("Template diperbarui");
      setEditTpl(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const replaceTplFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !editTpl) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.put(`/documents/templates/${editTpl.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`File diganti — ${r.data.placeholders.length} placeholder terdeteksi`);
      setEditTpl({ ...editTpl, placeholders: r.data.placeholders });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteTpl = async (t) => {
    if (!window.confirm(`Hapus template "${t.name}"? Dokumen yang sudah terbit tidak terpengaruh.`)) return;
    try {
      await api.delete(`/documents/templates/${t.id}`);
      toast.success("Template dihapus");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => {
    api.get("/documents/templates").then((r) => setTemplates(r.data));
    api.get("/documents/generated").then((r) => setGenerated(r.data));
    api.get("/sales/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const doUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("name", upForm.name || file.name);
    fd.append("doc_type", upForm.doc_type);
    fd.append("auto_on_booking", upForm.auto_on_booking ? "true" : "false");
    fd.append("file", file);
    try {
      const r = await api.post("/documents/templates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Template diunggah — ${r.data.placeholders.length} placeholder terdeteksi`);
      setShowUpload(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const generate = async () => {
    const tpl = templates.find((t) => t.id === genForm.template_id);
    try {
      if (tpl?.engine === "docx") {
        if (!genForm.booking_id && !genForm.project_id) return toast.error("Pilih sumber data: booking atau proyek");
        const r = await api.post("/documents/generate-docx", { template_id: tpl.id, booking_id: genForm.booking_id || null, project_id: genForm.project_id || null, overrides: pv?.fields || {} });
        toast.success(`Dokumen ${r.data.number} digenerate${r.data.has_pdf ? " (PDF siap)" : ""}`);
      } else {
        await api.post("/documents/generate", { template_id: genForm.template_id, booking_id: genForm.booking_id });
        toast.success("Dokumen digenerate");
      }
      setShowGen(false);
      setGenForm({ template_id: "", booking_id: "", project_id: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ttdAction = async (gid, action) => {
    try {
      const url = action === "minta" ? `/dokumen/${gid}/minta-ttd` : `/dokumen/${gid}/ttd/${action}`;
      const r = await api.post(url);
      toast.success(r.data.message);
      api.get("/documents/generated").then((res) => setGenerated(res.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const download = async (gid, fmt, number) => {
    try {
      const res = await api.get(`/documents/generated/${gid}/file?fmt=${fmt}`, { responseType: "blob" });      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${number}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e) || "File tidak tersedia"); }
  };

  const printDoc = (doc) => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${doc.template_name} - ${doc.number}</title><style>body{font-family:Georgia,serif;padding:48px;max-width:720px;margin:auto;white-space:pre-wrap;line-height:1.8;color:#111}h2{text-align:center}</style></head><body><h2>PT ABAD JAYA WAMPU</h2><hr/>${(doc.content || "").replace(/\n/g, "<br/>")}</body></html>`);
    w.document.close();
    w.print();
  };

  const selectedTpl = templates.find((t) => t.id === genForm.template_id);

  return (
    <div data-testid="dokumen-page">
      <PageHeader title="Dokumen" subtitle="Template Word (.docx) dengan placeholder {{...}} — auto-render & konversi PDF, tag tanda tangan untuk digital stamping" testid="dokumen-header">
        {isAdmin && (
          <Btn tone="ghost" onClick={() => setShowUpload(true)} data-testid="upload-docx-button"><Upload className="w-4 h-4" /> Upload Template .docx</Btn>
        )}
        {hasPerm("dokumen", "create") && (
          <Btn onClick={() => setShowGen(true)} data-testid="generate-doc-button"><FileText className="w-4 h-4" /> Generate Dokumen</Btn>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-sm font-mono uppercase tracking-wider text-slate-400 mb-3">Template</h2>
          <div className="space-y-3">
            {templates.map((t) => (
              <Card key={t.id} testid={`template-card-${t.id}`} className="!p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-200">{t.name}</div>
                    <div className="mt-1 flex gap-1.5 flex-wrap">
                      <Badge tone="indigo">{(t.doc_type || "lainnya").toUpperCase()}</Badge>
                      <Badge tone={t.engine === "docx" ? "emerald" : "slate"}>{t.engine === "docx" ? "WORD .docx" : "teks"}</Badge>
                      {(t.placeholders || []).some((p) => p.startsWith("ttd_")) && <Badge tone="amber"><FileSignature className="w-2.5 h-2.5 mr-1" />tag TTD</Badge>}
                    </div>
                    {t.engine === "docx" && (
                      <div className="flex gap-1 flex-wrap mt-2">
                        {(t.placeholders || []).map((p) => <code key={p} className="text-[9px] font-mono bg-slate-950 border border-slate-800 rounded px-1 py-0.5 text-emerald-400">{`{{${p}}}`}</code>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {t.engine !== "docx" && <Btn tone="ghost" className="!text-xs" onClick={() => setPreview(t)} data-testid={`template-preview-${t.id}`}>Pratinjau</Btn>}
                    {isAdmin && <Btn tone="amber" className="!text-xs" onClick={() => openEditTpl(t)} data-testid={`template-edit-${t.id}`}>Edit</Btn>}
                    {isAdmin && <Btn tone="danger" className="!text-xs" onClick={() => deleteTpl(t)} data-testid={`template-delete-${t.id}`}>Hapus</Btn>}
                  </div>
                </div>
              </Card>
            ))}
            {templates.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada template.</p></Card>}
          </div>
        </div>

        <div>
          <h2 className="text-sm font-mono uppercase tracking-wider text-slate-400 mb-3">Dokumen Terbit</h2>
          <DataTable testid="generated-docs-table" searchKeys={["number", "template_name"]}
            columns={[
              { header: "Nomor", accessor: "number", mono: true },
              { header: "Template", render: (r) => <span className="text-xs">{r.template_name || "-"}</span> },
              { header: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status || "final"}</Badge> },
              { header: "Dibuat", render: (r) => <span className="text-xs text-slate-500">{fmtDateTime(r.created_at)}</span> },
              { header: "Aksi", render: (r) => r.engine === "docx" ? (
                <div className="flex gap-1.5 flex-wrap">
                  <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => download(r.id, "pdf", r.number || r.id)} data-testid={`doc-download-pdf-${r.number || r.id}`}><Download className="w-3 h-3" /> PDF</Btn>
                  <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => download(r.id, "docx", r.number || r.id)} data-testid={`doc-download-docx-${r.number || r.id}`}>DOCX</Btn>
                  {r.signature_status === "ditandatangani" ? <Badge tone="emerald">Ditandatangani {fmtDateTime(r.customer_signed_at)}</Badge>
                    : r.signature_status === "dibatalkan" ? <Badge tone="slate">TTD dibatalkan</Badge>
                    : r.signature_required ? (
                      <>
                        <Badge tone="amber">Menunggu TTD</Badge>
                        <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => ttdAction(r.id, "ingatkan")} data-testid={`doc-ttd-remind-${r.id}`}>Ingatkan</Btn>
                        <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => ttdAction(r.id, "batal")} data-testid={`doc-ttd-cancel-${r.id}`}>Batalkan</Btn>
                      </>
                    ) : (
                      <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => ttdAction(r.id, "minta")} data-testid={`doc-ttd-request-${r.id}`}><FileSignature className="w-3 h-3" /> Minta TTD</Btn>
                    )}
                </div>
              ) : (
                <div className="flex gap-1.5">
                  <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => setPreview(r)} data-testid={`doc-view-${r.number || r.id}`}>Lihat</Btn>
                  <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => printDoc(r)} data-testid={`doc-print-${r.number || r.id}`}><Printer className="w-3 h-3" /></Btn>
                </div>
              ) },
            ]}
            rows={generated} empty="Belum ada dokumen terbit" />
        </div>
      </div>

      <Modal open={showUpload} onClose={() => setShowUpload(false)} title="Upload Template Word (.docx)" testid="upload-docx-modal">
        <div className="space-y-3">
          <Field label="Nama Template"><input className={inputCls} placeholder="PPJB Word" value={upForm.name} onChange={(e) => setUpForm({ ...upForm, name: e.target.value })} data-testid="upload-name-input" /></Field>
          <Field label="Tipe Dokumen">
            <select className={inputCls} value={upForm.doc_type} onChange={(e) => setUpForm({ ...upForm, doc_type: e.target.value })} data-testid="upload-doctype-select">
              <option value="spk">SPK (auto-terbit saat proyek dibuat)</option>
              <option value="ppjb">PPJB</option>
              <option value="kwitansi">Kwitansi</option>
              <option value="lainnya">Lainnya</option>
            </select>
          </Field>
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-slate-400 space-y-1">
            <div className="text-slate-300 font-medium">Placeholder yang dikenali:</div>
            <div className="font-mono text-[10px] text-emerald-400">{"{{nama_konsumen}} {{telepon_konsumen}} {{nama_lokasi}} {{alamat_lokasi}} {{blok_rumah}} {{nomor_unit}} {{blok_unit}} {{tipe_unit}} {{harga_jadi}} {{booking_fee}} {{dp_nominal}} {{cara_bayar}} {{id_proyek}} {{nama_proyek}} {{unit_proyek}} {{rab_total}} {{tanggal}} {{nomor_dokumen}}"}</div>
            <div className="text-slate-300 font-medium mt-2">Dari Formulir Booking (CRM):</div>
            <div className="font-mono text-[10px] text-cyan-400">{"{{nama_pemohon}} {{no_hp_pemohon}} {{ttl_pemohon}} {{jenis_usaha}} {{penghasilan_pemohon}} {{penghasilan_pemohon_terbilang}} {{nik_pemohon}} {{alamat_pemohon}} {{nama_pasangan}} {{ttl_pasangan}} {{pekerjaan_pasangan}} {{alamat_pasangan}} {{nama_perumahan}} {{blok_nomor_unit}} {{luas_bangunan}} {{alamat_rumah}} {{harga_jual_rumah}} {{harga_jual_rumah_terbilang}} {{besaran_dp}} {{besaran_dp_terbilang}} {{dp_dibayar}} {{dp_dibayar_terbilang}} {{kekurangan_dp}} {{kekurangan_dp_terbilang}} {{nilai_kpr}} {{nilai_kpr_terbilang}} {{cabang_bank}} {{ttd_cabang_bank}} {{tempat_tanda_tangan}} {{tanggal_form}}"}</div>
            <div className="text-slate-300 font-medium mt-2">Tag tanda tangan (sesuai role approver):</div>
            <div className="font-mono text-[10px] text-amber-400">{"{{ttd_mandor}} {{ttd_manajer_proyek}} {{ttd_direktur}} {{ttd_finance}}"}</div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={upForm.auto_on_booking} onChange={(e) => setUpForm({ ...upForm, auto_on_booking: e.target.checked })} className="w-4 h-4 accent-emerald-500" data-testid="upload-auto-booking-check" />
            Auto-generate saat Formulir Booking di-submit (CRM)
          </label>
          <p className="text-[11px] text-slate-500">Placeholder dengan sintaks ${"..."} (Word lama) otomatis dinormalisasi menjadi {"{{...}}"} saat diunggah.</p>
          <input ref={fileRef} type="file" accept=".docx" className="hidden" onChange={doUpload} data-testid="upload-file-input" />
          <Btn className="w-full justify-center" onClick={() => fileRef.current.click()} data-testid="upload-select-file-button"><Upload className="w-4 h-4" /> Pilih File .docx</Btn>
        </div>
      </Modal>

      <Modal open={showGen} onClose={() => setShowGen(false)} title="Generate Dokumen" testid="generate-form-modal">
        <div className="space-y-3">
          <Field label="Template">
            <select className={inputCls} value={genForm.template_id} onChange={(e) => setGenForm({ template_id: e.target.value, booking_id: "", project_id: "" })} data-testid="gen-template-select">
              <option value="">Pilih template...</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name} {t.engine === "docx" ? "(Word)" : "(Teks)"}</option>)}
            </select>
          </Field>
          {selectedTpl?.engine === "docx" && (
            <Field label="Sumber Data">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select className={inputCls} value={genForm.booking_id} onChange={(e) => setGenForm({ ...genForm, booking_id: e.target.value, project_id: "" })} data-testid="gen-booking-select">
                  <option value="">Dari Booking...</option>
                  {bookings.filter((b) => b.status !== "batal").map((b) => <option key={b.id} value={b.id}>{b.number} — {b.customer_name}</option>)}
                </select>
                <select className={inputCls} value={genForm.project_id} onChange={(e) => setGenForm({ ...genForm, project_id: e.target.value, booking_id: "" })} data-testid="gen-project-select">
                  <option value="">Dari Proyek Konstruksi...</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </Field>
          )}
          {selectedTpl && selectedTpl.engine !== "docx" && (
            <Field label="Sumber Data (Booking)">
              <select className={inputCls} value={genForm.booking_id} onChange={(e) => setGenForm({ ...genForm, booking_id: e.target.value })} data-testid="gen-booking-select-text">
                <option value="">Pilih booking...</option>
                {bookings.filter((b) => b.status !== "batal").map((b) => <option key={b.id} value={b.id}>{b.number} — {b.customer_name}</option>)}
              </select>
            </Field>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowGen(false)}>Batal</Btn>
            {selectedTpl?.engine === "docx" && (genForm.booking_id || genForm.project_id) && (
              <Btn tone="amber" onClick={openPreview} data-testid="gen-preview-button"><Eye className="w-4 h-4" /> Pratinjau & Edit</Btn>
            )}
            <Btn onClick={generate} data-testid="gen-save-button">Generate</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!pv} onClose={() => setPv(null)} title="Pratinjau & Edit Dokumen" wide testid="doc-generate-preview-modal">
        {pv && (
          <div className="space-y-3">
            {pv.ttd_tags?.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2 text-xs text-amber-300" data-testid="preview-ttd-notice">
                Template ini memiliki tag tanda tangan ({pv.ttd_tags.map((t) => `{{${t}}}`).join(" ")}) — dokumen akan masuk alur approval tanda tangan sesuai role pada tag.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[35vh] overflow-y-auto pr-1" data-testid="preview-fields">
              {Object.entries(pv.fields).map(([k, v]) => (
                <Field key={k} label={k}>
                  <input className={inputCls} value={v} onChange={(e) => setPv({ ...pv, fields: { ...pv.fields, [k]: e.target.value } })} data-testid={`pv-field-${k}`} />
                </Field>
              ))}
            </div>
            <div className="flex gap-2">
              <Btn tone="ghost" onClick={renderPreview} data-testid="pv-render-button">Render Pratinjau PDF</Btn>
            </div>
            {pvUrl && <iframe src={pvUrl} title="pratinjau" className="w-full h-[50vh] rounded-lg border border-slate-700 bg-white" data-testid="pv-iframe" />}
            <div className="flex justify-end gap-2 pt-1 border-t border-slate-800">
              <Btn tone="ghost" onClick={() => setPv(null)}>Tutup</Btn>
              <Btn onClick={() => { setPv(null); generate(); }} data-testid="pv-generate-button">Generate Final (DOCX + PDF)</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview?.name || preview?.template_name || "Pratinjau"} wide testid="doc-preview-modal">
        {preview && (
          <div className="bg-white text-slate-900 rounded-lg p-8 whitespace-pre-wrap font-serif text-sm leading-relaxed" data-testid="doc-preview-content">
            <div className="text-center font-bold text-lg mb-1">PT ABAD JAYA WAMPU</div>
            <hr className="my-4 border-slate-300" />
            {preview.content}
          </div>
        )}
      </Modal>

      <Modal open={!!editTpl} onClose={() => setEditTpl(null)} title={`Edit Template — ${editTpl?.name || ""}`} testid="edit-template-modal">
        {editTpl && (
          <div className="space-y-3">
            <Field label="Nama Template">
              <input className={inputCls} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name-input" />
            </Field>
            <Field label="Tipe Dokumen">
              <select className={inputCls} value={editForm.doc_type} onChange={(e) => setEditForm({ ...editForm, doc_type: e.target.value })} data-testid="edit-doctype-select">
                {[...new Set(["spk", "ppjb", "kwitansi", "lainnya", editForm.doc_type])].map((v) => <option key={v} value={v}>{(v || "lainnya").toUpperCase()}</option>)}
              </select>
            </Field>
            {editTpl.engine !== "docx" && (
              <Field label="Konten Template">
                <textarea className={inputCls + " min-h-[160px] font-mono text-xs"} value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} data-testid="edit-content-textarea" />
              </Field>
            )}
            {editTpl.engine === "docx" && (
              <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-2">
                <div className="text-xs text-slate-400">File saat ini: <span className="font-mono text-slate-200">{editTpl.filename || "-"}</span></div>
                {(editTpl.placeholders || []).length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {editTpl.placeholders.map((p) => <code key={p} className="text-[9px] font-mono bg-slate-900 border border-slate-800 rounded px-1 py-0.5 text-emerald-400">{`{{${p}}}`}</code>)}
                  </div>
                )}
                <input ref={replaceFileRef} type="file" accept=".docx" className="hidden" onChange={replaceTplFile} data-testid="edit-replace-file-input" />
                <Btn tone="ghost" className="!text-xs" onClick={() => replaceFileRef.current.click()} data-testid="edit-replace-file-button"><Upload className="w-3.5 h-3.5" /> Ganti File .docx</Btn>
                <p className="text-[10px] text-slate-500">File disimpan di object storage — mengganti file akan menormalisasi placeholder dan membaca ulang tag secara otomatis.</p>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={editForm.auto_on_booking} onChange={(e) => setEditForm({ ...editForm, auto_on_booking: e.target.checked })} className="w-4 h-4 accent-emerald-500" data-testid="edit-auto-booking-check" />
              Auto-generate saat Formulir Booking di-submit (CRM)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Btn tone="ghost" onClick={() => setEditTpl(null)} data-testid="edit-cancel-button">Batal</Btn>
              <Btn onClick={saveEditTpl} data-testid="edit-save-button">Simpan Perubahan</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
